import dayjs from 'dayjs';
import {
  clampMetricDateRange,
  getMetricEffectiveStartDate,
} from '@/common/utils/metric-effective-period';

export const FACT_REPORT_SORTS = {
  amountDesc: 'amountDesc',
  configOrder: 'configOrder',
  nameAsc: 'nameAsc',
};

const FACT_REPORT_DRILLDOWN_DOMAINS = [
  { key: 'plan', label: '经营计划' },
  { key: 'income', label: '经营收入' },
  { key: 'salary', label: '门店工资' },
  { key: 'expense', label: '经营费用' },
];

const HEADQUARTERS_EXPENSE_ATTRIBUTION_TYPE = 'headquarters_management_expense';
const METRIC_EXPLANATION_ROLES = new Set(['owner', 'admin']);
const HEADQUARTERS_SOURCE_CLASSIFICATION = 'headquartersSource';
const HEADQUARTERS_PARENT_CLASSIFICATION = 'headquartersParent';

const FACT_REPORT_DRILLDOWN_COLUMN_GROUP_ORDER = ['entity', 'date', 'value', 'other'];
const FACT_REPORT_DRILLDOWN_COLUMN_GROUPS = {
  plan: {
    entity: [{ dataIndex: 'operationEntityName', key: 'operationEntityName', title: '经营实体' }],
    date: [
      {
        dataIndex: 'planStartedAt',
        format: 'planPeriod',
        key: 'planStartedAt',
        title: '计划周期',
      },
    ],
    value: [
      { dataIndex: ['sourceValue', 'text'], key: 'sourceValue', title: '计划金额' },
      { dataIndex: ['projectedValue', 'text'], key: 'projectedValue', title: '周期摊分金额' },
    ],
    other: [{ dataIndex: 'planItemName', key: 'planItemName', title: '计划项目' }],
  },
  income: {
    entity: [{ dataIndex: 'storeName', key: 'storeName', title: '门店名称' }],
    date: [{ dataIndex: 'incomeDate', format: 'date', key: 'incomeDate', title: '收入日期' }],
    value: [{ dataIndex: ['sourceValue', 'text'], key: 'sourceValue', title: '收入值' }],
    other: [
      { dataIndex: 'incomeTypeLevel1', key: 'incomeTypeLevel1', title: '一级收入类型' },
      { dataIndex: 'incomeTypeLevel2', key: 'incomeTypeLevel2', title: '二级收入类型' },
      { dataIndex: 'incomeTypeLevel3', key: 'incomeTypeLevel3', title: '三级收入类型' },
    ],
  },
  salary: {
    entity: [{ dataIndex: 'storeName', key: 'storeName', title: '门店名称' }],
    date: [
      {
        dataIndex: 'salaryStartedAt',
        format: 'salaryPeriod',
        key: 'salaryStartedAt',
        title: '工资周期',
      },
    ],
    value: [
      { dataIndex: ['sourceValue', 'text'], key: 'sourceValue', title: '工资值' },
      { dataIndex: ['projectedValue', 'text'], key: 'projectedValue', title: '周期计入值' },
    ],
    other: [
      { dataIndex: 'salaryTypeLevel1', key: 'salaryTypeLevel1', title: '一级工资类型' },
      { dataIndex: 'salaryTypeLevel2', key: 'salaryTypeLevel2', title: '二级工资类型' },
      { dataIndex: 'salaryTypeLevel3', key: 'salaryTypeLevel3', title: '三级工资类型' },
    ],
  },
  expense: {
    entity: [
      {
        dataIndex: 'sourceCollectionType',
        format: 'sourceCollectionType',
        key: 'sourceCollectionType',
        title: '数据来源',
      },
      { dataIndex: 'departmentLevel2', key: 'departmentLevel2', title: '2级部门' },
      { dataIndex: 'departmentLevel3', key: 'departmentLevel3', title: '3级部门' },
      { dataIndex: 'expenseDepartment', key: 'expenseDepartment', title: '费用承担部门' },
    ],
    date: [
      { dataIndex: 'paidAt', format: 'date', key: 'paidAt', title: '支付日期' },
      { dataIndex: 'rentStartedAt', format: 'date', key: 'rentStartedAt', title: '租金日期-起' },
      { dataIndex: 'rentEndedAt', format: 'date', key: 'rentEndedAt', title: '租金日期-止' },
      { dataIndex: 'submittedAt', format: 'date', key: 'submittedAt', title: '提交日期' },
    ],
    value: [
      { dataIndex: 'eventAmountText', key: 'eventAmountText', title: '支付金额' },
      { dataIndex: ['projectedValue', 'text'], key: 'projectedValue', title: '周期摊分金额' },
    ],
    other: [
      { dataIndex: 'documentNo', key: 'documentNo', title: '单号' },
      { dataIndex: 'expenseTypeLevel1', key: 'expenseTypeLevel1', title: '1级费用类型' },
      { dataIndex: 'expenseTypeLevel2', key: 'expenseTypeLevel2', title: '2级费用类型' },
      { dataIndex: 'expenseTypeLevel3', key: 'expenseTypeLevel3', title: '3级费用类型' },
      {
        dataIndex: 'allocationType',
        format: 'allocationType',
        key: 'allocationType',
        title: '生效策略',
      },
      { dataIndex: 'title', key: 'title', title: '标题' },
      { dataIndex: 'submitter', key: 'submitter', title: '提单人' },
      { dataIndex: 'payeeInfo', format: 'payeeInfo', key: 'payeeInfo', title: '收款信息' },
    ],
  },
};

const SORT_SEQUENCE = [
  FACT_REPORT_SORTS.configOrder,
  FACT_REPORT_SORTS.amountDesc,
  FACT_REPORT_SORTS.nameAsc,
];

const getSectionControlKey = (section, index = 0) =>
  section?.key || section?.title || `section-${index}`;

const getDefaultSectionControl = (section = {}) => ({
  hideZeroAmount: Boolean(section.summaryItems?.length),
  sortMode: section.summaryItems?.length
    ? FACT_REPORT_SORTS.amountDesc
    : FACT_REPORT_SORTS.configOrder,
});

export const getFactReportSectionControls = (sections = [], current = {}) => {
  const next = {};
  sections.forEach((section, index) => {
    const sectionKey = getSectionControlKey(section, index);
    next[sectionKey] = current[sectionKey] || getDefaultSectionControl(section);
  });
  const currentEntries = Object.entries(current);
  const nextEntries = Object.entries(next);
  const unchanged =
    currentEntries.length === nextEntries.length &&
    currentEntries.every(
      ([key, control]) =>
        next[key]?.hideZeroAmount === control.hideZeroAmount &&
        next[key]?.sortMode === control.sortMode,
    );
  return unchanged ? current : next;
};

export const getSectionControl = (
  section,
  sectionIndex,
  {
    hideZeroAmount = false,
    sortMode = FACT_REPORT_SORTS.configOrder,
    sectionControls = {},
    sectionSortModes = {},
  } = {},
) => {
  const sectionKey = getSectionControlKey(section, sectionIndex);
  if (sectionControls?.[sectionKey]) {
    return sectionControls[sectionKey];
  }
  return {
    hideZeroAmount,
    sortMode: sectionSortModes?.[sectionKey] || sortMode,
  };
};

export const getFactReportDateParams = ({ endDate, startDate }, today, effectiveStartAt = null) => {
  const yesterday = dayjs(today).subtract(1, 'day');
  const selectedRange =
    startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : [yesterday, yesterday];
  const [normalizedStart, normalizedEnd] = clampMetricDateRange(
    selectedRange,
    getMetricEffectiveStartDate(effectiveStartAt),
  );
  return {
    endDate: normalizedEnd.format('YYYY-MM-DD'),
    startDate: normalizedStart.format('YYYY-MM-DD'),
  };
};

const getQueryValue = (value) => (Array.isArray(value) ? value.at(-1) : value);

export const getFactReportQueryPatch = (query = {}, today, effectiveStartAt = null) => {
  const { endDate, startDate } = getFactReportDateParams(
    {
      endDate: getQueryValue(query.endDate),
      startDate: getQueryValue(query.startDate),
    },
    today,
    effectiveStartAt,
  );
  return {
    ...(getQueryValue(query.endDate) === endDate ? {} : { endDate }),
    ...(getQueryValue(query.startDate) === startDate ? {} : { startDate }),
  };
};

export const getFactReportParams = ({ reportDateParams = {}, scope } = {}) => ({
  endDate: reportDateParams.endDate,
  scope,
  startDate: reportDateParams.startDate,
});

export const getFactReportMetricDisplayName = (name = '') => {
  const dashIndex = name.indexOf('-');
  return dashIndex >= 0 ? name.slice(dashIndex + 1) : name;
};

export const getFactReportCalculationStoreText = (context) => {
  if (context?.scope === 'aggregate') {
    return '汇总';
  }
  const entityPath = context?.entityPath
    ?.map((entity) => entity?.name)
    .filter(Boolean)
    .join(' / ');
  return entityPath || context?.entityName || context?.storeName || '-';
};

const getFactReportMetricValue = (item) => item.value;

export const getFactReportStageText = (stage) => {
  if (stage === 'forecast') {
    return '推测';
  }
  return stage === 'actual' ? '实际' : '-';
};

export const getFactReportMetricStageText = (item) =>
  getFactReportStageText(getFactReportMetricValue(item)?.stage);

const getSelectionEvidenceTypes = (candidate, selection) => {
  if (selection.stage === 'forecast' && candidate.statisticalModelType) {
    return ['forecast'];
  }
  if (candidate.metricType === 'composite') {
    if (candidate.expressionType === 'first_available') {
      return ['selection'];
    }
    return candidate.expressionType === 'arithmetic' ? ['calculation'] : [];
  }
  return candidate.drilldownDomains?.length ? ['source'] : [];
};

export const getFactReportCalculationSelections = (calculation = {}) => {
  const candidatesById = new Map(
    (calculation.candidates || []).map((candidate) => [candidate.metricId, candidate]),
  );
  return (calculation.selections || []).map((selection, index) => {
    const candidate = candidatesById.get(selection.selectedMetricId);
    const metric = candidate
      ? {
          ...candidate,
          explanation: {
            evidenceTypes: getSelectionEvidenceTypes(candidate, selection),
          },
          ...(selection.stage === 'forecast' && candidate.statisticalModelType
            ? { forecastSource: 'model' }
            : {}),
          name: candidate.metricName,
          value: selection.value,
        }
      : undefined;
    return {
      key: `${selection.storeId || 'summary'}:${selection.selectedMetricId}:${index}`,
      ...(metric ? { metric } : {}),
      metricName:
        candidate?.metricName || selection.selectedMetricName || selection.selectedMetricId || '-',
      stage: getFactReportStageText(selection.stage || selection.value?.stage),
      store: selection.storeName || (selection.storeId ? selection.storeId : '汇总'),
      value: selection.value?.text ?? '-',
    };
  });
};

const getFiniteMetricValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getFactReportMetricDisplayValue = (item) => {
  const metricValue = getFactReportMetricValue(item);
  const value = getFiniteMetricValue(metricValue?.value);
  if (value === null) {
    return metricValue?.text ?? '-';
  }
  const formattedValue = value.toLocaleString('zh-CN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  return item.valueType === 'ratio' ? `${formattedValue}%` : formattedValue;
};

export const getFactReportMetricClassification = (item = {}) => {
  if (item.attributionType === HEADQUARTERS_EXPENSE_ATTRIBUTION_TYPE) {
    return HEADQUARTERS_SOURCE_CLASSIFICATION;
  }
  if (
    item.explanation?.dependencyRefs?.some(
      ({ attributionType }) => attributionType === HEADQUARTERS_EXPENSE_ATTRIBUTION_TYPE,
    )
  ) {
    return HEADQUARTERS_PARENT_CLASSIFICATION;
  }
  return 'normal';
};

export const getFactReportMetricAccess = (classification, role) => {
  if (METRIC_EXPLANATION_ROLES.has(role)) {
    return { drillable: true, visible: true };
  }
  if (classification === HEADQUARTERS_SOURCE_CLASSIFICATION) {
    return { drillable: false, visible: false };
  }
  return {
    drillable: classification !== HEADQUARTERS_PARENT_CLASSIFICATION,
    visible: true,
  };
};

export const getFactReportMetricActionType = (item, role) => {
  if (!getFactReportMetricAccess(getFactReportMetricClassification(item), role).drillable) {
    return 'none';
  }
  if (item.explanation) {
    return item.metricType === 'composite' || item.explanation.evidenceTypes?.length
      ? 'explanation'
      : 'none';
  }
  if (item.value?.stage === 'forecast') {
    if (item.forecastSource === 'model' && item.metricId) {
      return 'forecastEvidence';
    }
    return 'none';
  }
  if (item.metricType === 'composite') {
    return 'none';
  }
  if (
    item.metricType !== 'atomic' ||
    !item.metricId ||
    (item.valueType === 'ratio' &&
      !item.drilldownDomains?.some((domain) => domain === 'plan' || domain === 'salary'))
  ) {
    return 'none';
  }
  return getFactReportDrilldownTabs(item).length ? 'drilldown' : 'none';
};

export const getFactReportDrilldownTabs = (item = {}) => {
  const evidenceTypes = Array.isArray(item?.evidenceSections)
    ? item.evidenceSections.map(({ evidenceType }) => evidenceType)
    : item?.drilldownDomains;
  const domains = new Set(Array.isArray(evidenceTypes) ? evidenceTypes : []);
  return FACT_REPORT_DRILLDOWN_DOMAINS.filter(({ key }) => domains.has(key));
};

export const getFactReportEvidenceSection = (sections = [], evidenceType) =>
  sections.find((section) => section.evidenceType === evidenceType) || {};

export const getFactReportEvidencePagination = (section = {}, fallbackCurrent = 1) => ({
  current: section.meta?.current || fallbackCurrent,
  pageSize: section.meta?.pageSize || 20,
  showSizeChanger: false,
  total: section.meta?.total || 0,
});

export const getFactReportDrilldownContextKey = (organizationId, pageParams, metric) =>
  JSON.stringify([organizationId || null, pageParams || null, metric?.metricId || null]);

export const appendFactReportDrilldownPath = (path, childIndex) => [...path, childIndex];

export const popFactReportDrilldownPath = (path) => path.slice(0, -1);

export const getFactReportModelForecast = (forecast) =>
  forecast?.source === 'model' ? forecast : null;

export const getFactReportDrilldownColumns = (sourceDomain, metric = {}) => {
  const groups = FACT_REPORT_DRILLDOWN_COLUMN_GROUPS[sourceDomain];
  if (!groups) {
    return [];
  }
  const columns = FACT_REPORT_DRILLDOWN_COLUMN_GROUP_ORDER.flatMap((group) => groups[group]);
  if (
    sourceDomain === 'salary' &&
    metric.attributionType === HEADQUARTERS_EXPENSE_ATTRIBUTION_TYPE
  ) {
    return [
      { ...columns[0], title: '工资来源部门' },
      { dataIndex: 'allocationDepartment', key: 'allocationDepartment', title: '分摊承担部门' },
      columns[1],
      { key: 'effectivePeriod', title: '本次计入周期', format: 'attributionPeriod' },
      ...columns.slice(2),
      {
        dataIndex: ['attribution', 'sourceAllocationType'],
        key: 'sourceAllocationType',
        title: '生效策略',
        format: 'allocationType',
      },
    ];
  }
  if (
    sourceDomain !== 'expense' ||
    metric.attributionType !== HEADQUARTERS_EXPENSE_ATTRIBUTION_TYPE
  ) {
    return columns;
  }
  const departmentIndex = columns.findIndex(({ key }) => key === 'expenseDepartment');
  return [
    ...columns.slice(0, departmentIndex + 1),
    {
      dataIndex: 'allocationDepartment',
      key: 'allocationDepartment',
      title: '分摊承担部门',
    },
    ...columns.slice(departmentIndex + 1),
  ];
};

export const getNextFactReportSort = (sortMode) => {
  const currentIndex = SORT_SEQUENCE.indexOf(sortMode);
  return SORT_SEQUENCE[(currentIndex + 1) % SORT_SEQUENCE.length];
};

export const getFactReportPageLoading = ({ isFetching = false, isPending = false } = {}) =>
  Boolean(isPending || isFetching);

const getItemValue = (item) => getFiniteMetricValue(getFactReportMetricValue(item)?.value) ?? 0;

const isZeroMetricValue = (item) =>
  getFiniteMetricValue(getFactReportMetricValue(item)?.value) === 0;

const sortItems = (items, sortMode) => {
  if (sortMode === FACT_REPORT_SORTS.configOrder) {
    return [...items];
  }
  return [...items].sort((prev, next) => {
    if (sortMode === FACT_REPORT_SORTS.amountDesc) {
      return getItemValue(next) - getItemValue(prev);
    }
    return getFactReportMetricDisplayName(prev.name).localeCompare(
      getFactReportMetricDisplayName(next.name),
      'zh-Hans-CN',
    );
  });
};

const filterItems = (items, hideZeroAmount) =>
  hideZeroAmount ? items.filter((item) => !isZeroMetricValue(item)) : items;

const getSectionChildItems = (section) =>
  section.rows?.length ? section.rows.flat() : section.items || [];

export const getFactReportAccessibleSections = (sections = [], role) => {
  if (METRIC_EXPLANATION_ROLES.has(role)) {
    return sections;
  }
  const isVisible = (item) =>
    getFactReportMetricAccess(getFactReportMetricClassification(item), role).visible;
  return sections
    .map((section) => ({
      ...section,
      ...(section.items ? { items: section.items.filter(isVisible) } : {}),
      ...(section.rows
        ? { rows: section.rows.map((row) => row.filter(isVisible)).filter((row) => row.length) }
        : {}),
      ...(section.summaryItems ? { summaryItems: section.summaryItems.filter(isVisible) } : {}),
    }))
    .filter(hasVisibleSectionItems);
};

export const getFactReportVisibleSummaryItems = (section, { hideZeroAmount = false } = {}) =>
  filterItems(section.summaryItems || [], hideZeroAmount);

const hasVisibleSectionItems = (section) =>
  Boolean(
    (section?.summaryItems && section.summaryItems.length) || getSectionChildItems(section).length,
  );

export const getFactReportVisibleSections = (sections = []) =>
  sections.filter((section) => hasVisibleSectionItems(section));

export const getFactReportSectionSortMode = (
  section,
  { sectionSortModes = {}, sortMode = FACT_REPORT_SORTS.configOrder } = {},
) => sectionSortModes?.[section?.key] || sortMode;

const toDescriptionItem = (item, { renderValue, sequenceNumber }) => {
  const displayValue = getFactReportMetricDisplayValue(item);
  const displayName = getFactReportMetricDisplayName(item.name);
  return {
    children: renderValue ? renderValue(item, displayValue) : displayValue,
    key: item.key,
    label: sequenceNumber ? `${sequenceNumber}. ${displayName}` : displayName,
  };
};

const getDisplayItems = (items, options) =>
  sortItems(filterItems(items, options.hideZeroAmount), options.sortMode);

export const getExportItemRows = (section, options) => {
  const rows = section.rows?.length
    ? section.rows.flatMap((row) =>
        sortItems(filterItems(row, options.hideZeroAmount), options.sortMode),
      )
    : sortItems(filterItems(section.items || [], options.hideZeroAmount), options.sortMode);
  return rows.map((item) => [
    `${section.title} - ${getFactReportMetricDisplayName(item.name)}`,
    getFactReportMetricDisplayValue(item),
    getFactReportMetricStageText(item),
  ]);
};

const escapeCsvCell = (value) => {
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

const toCsvLine = (cells) => cells.map(escapeCsvCell).join(',');

export const getFactReportDescriptionItems = (
  section,
  { hideZeroAmount = false, renderValue, sortMode = FACT_REPORT_SORTS.configOrder } = {},
) => {
  const options = { hideZeroAmount, renderValue, sortMode };
  const visibleItems = section.rows?.length
    ? section.rows.flatMap((row) => getDisplayItems(row, options))
    : getDisplayItems(section.items || [], options);
  const hasSummaryTitleMetrics = Boolean(section.summaryItems?.length);
  return visibleItems.map((item, index) =>
    toDescriptionItem(item, {
      ...options,
      sequenceNumber: hasSummaryTitleMetrics ? index + 1 : undefined,
    }),
  );
};

export const getFactReportExportCsv = (
  sections = [],
  {
    hideZeroAmount = false,
    sectionSortModes = {},
    sectionControls = {},
    sortMode = FACT_REPORT_SORTS.configOrder,
  } = {},
) => {
  const rows = sections.flatMap((section, sectionIndex) => {
    const sectionControl = getSectionControl(section, sectionIndex, {
      hideZeroAmount,
      sortMode,
      sectionControls,
      sectionSortModes,
    });
    if (!hasVisibleSectionItems(section, sectionControl.hideZeroAmount)) {
      return [];
    }

    const summaryOptions = {
      ...sectionControl,
      hideZeroAmount: sectionControl.hideZeroAmount,
    };
    return [
      ...getFactReportVisibleSummaryItems(section, summaryOptions).map((item) => [
        `${section.title} - ${getFactReportMetricDisplayName(item.name)}`,
        getFactReportMetricDisplayValue(item),
        getFactReportMetricStageText(item),
      ]),
      ...getExportItemRows(section, {
        ...sectionControl,
        sortMode: sectionControl.sortMode,
      }),
    ];
  });
  return ['\uFEFF指标,数值,阶段', ...rows.map(toCsvLine)].join('\n');
};

export const getFactReportExportFilename = ({ endDate, startDate } = {}) =>
  startDate && endDate ? `损益报表_${startDate}_${endDate}.csv` : '损益报表.csv';
